// 1. React & Hooks
import React, { useState, useEffect, useCallback } from "react";
// 2. Material UI Components
import {
  Typography,
  Box,
  Grid,
  Paper,
  InputBase,
  Card,
  CardMedia,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Avatar,
  Snackbar,
  Alert
} from "@mui/material";
// 3. Routing
import { useNavigate, useLocation } from "react-router-dom";
// 4. Icons
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// 5. Local Imports
import API from "../../api/api";
import AddressDrawer from "../../components/AddressDrawer";
import Footer from "../../components/footer";
import GlobalSkeleton from "../../components/GlobalSkeleton";
import { getCachedData, setCachedData } from "../../utils/cache";
import { keyframes } from "@mui/material";
export default function LandingHome() {
  const location = useLocation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (location.state?.orderSuccess) {
      setShowSuccess(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [cart, setCart] = useState([]);
  const [viewDetails, setViewDetails] = useState(false);
  const [tempQuantity, setTempQuantity] = useState(1);
  // ---> FOOD TAGS STATE <---
  const [foodTags, setFoodTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const syncCart = useCallback(() => {
    const saved = localStorage.getItem("mealnoCart");
    setCart(saved ? JSON.parse(saved) : []);
  }, []);
  useEffect(() => {
    syncCart();
    window.addEventListener("storage", syncCart);
    return () => window.removeEventListener("storage", syncCart);
  }, [syncCart]);
  const updateQty = (item, change) => {
    let current = [...cart];
    const idx = current.findIndex(i => i.id === item.id);
    if (idx !== -1) {
      current[idx] = { ...current[idx], quantity: current[idx].quantity + change };
      if (current[idx].quantity <= 0) current.splice(idx, 1);
      localStorage.setItem("mealnoCart", JSON.stringify(current));
      setCart(current);
      window.dispatchEvent(new Event("storage"));
    }
  };
  const navigate = useNavigate();
  const [currentAddress, setCurrentAddress] = useState(null);
  const [openSheet, setOpenSheet] = useState(false);
  const [popularRestaurants, setPopularRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const heroTexts = [
    "Healthy food from trusted home chefs.",
    "Homemade food, at your doorstep.",
    "Freshly cooked meals, made with love."
  ];
  const [heroIndex, setHeroIndex] = useState(0);
  const fadeText = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
  `;
  // ---> FIX 2: Helper to compute discount % from display_price vs selling_price <---
  const getDiscountPercent = (displayPrice, sellingPrice) => {
    if (!displayPrice || !sellingPrice || displayPrice <= sellingPrice) return 0;
    return Math.round(((displayPrice - sellingPrice) / displayPrice) * 100);
  };
  const fetchPopularRestaurants = useCallback(async (lat, lng) => {
    if (!lat || !lng) return;
    const cacheKeyMenu = `popular_${lat}_${lng}`;
    const cacheKeyTags = `tags_${lat}_${lng}`;
    const cachedMenu = getCachedData(cacheKeyMenu);
    const cachedTags = getCachedData(cacheKeyTags);
    if (cachedMenu && cachedTags) {
      setPopularRestaurants(cachedMenu);
      setFoodTags(cachedTags);
    } else {
      setLoading(true);
      setLoadingTags(true);
    }
    try {
      const res = await API.post("/menu-by-location", { lat, lng });
      if (res.data.menu) {
        setPopularRestaurants(res.data.menu);
        setCachedData(cacheKeyMenu, res.data.menu);
        if (res.data.tags) {
          setFoodTags(res.data.tags);
          setCachedData(cacheKeyTags, res.data.tags);
        }
      }
    } catch (err) {
      console.error("Error fetching local popular items:", err);
      setPopularRestaurants([]);
      setFoodTags([]);
    } finally {
      setLoading(false);
      setLoadingTags(false);
    }
  }, []);
  const [selectedItem, setSelectedItem] = useState(null);
  useEffect(() => {
    const isAnyModalOpen = openSheet || viewDetails || !!selectedItem;
    if (isAnyModalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    window.dispatchEvent(new Event("modal-state-change"));
    return () => {
      document.body.classList.remove("modal-open");
      window.dispatchEvent(new Event("modal-state-change"));
    };
  }, [openSheet, viewDetails, selectedItem]);
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroTexts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroTexts.length]);
  const loadAddressData = useCallback(() => {
    const saved = localStorage.getItem("selectedAddress");
    if (saved) {
      const addr = JSON.parse(saved);
      setCurrentAddress(addr);
      fetchPopularRestaurants(addr.lat, addr.lng);
    } else {
      const fetchDefault = async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;
        try {
          const res = await API.get(`/user/${user.id}/addresses`);
          const defaultAddr = res.data.find(a => a.is_default);
          if (defaultAddr) {
            setCurrentAddress(defaultAddr);
            fetchPopularRestaurants(defaultAddr.lat, defaultAddr.lng);
            localStorage.setItem("selectedAddress", JSON.stringify(defaultAddr));
          }
        } catch (err) { console.log(err); }
      };
      fetchDefault();
    }
  }, [fetchPopularRestaurants]);
  useEffect(() => {
    loadAddressData();
    const handleUpdate = (e) => {
      if (!e || !e.key || e.key === "mealnoCart") return;
      loadAddressData();
    };
    window.addEventListener("storage", handleUpdate);
    return () => window.removeEventListener("storage", handleUpdate);
  }, [loadAddressData]);
  const handleAddToCart = (item, qty = 1, e = null) => {
    if (e) e.stopPropagation();
    
    let currentCart = JSON.parse(localStorage.getItem("mealnoCart")) || [];
    const existingItemIndex = currentCart.findIndex(cartItem => cartItem.id === item.id);
    if (existingItemIndex !== -1) {
      currentCart[existingItemIndex].quantity += qty;
    } else {
      currentCart.push({
        id: item.id,
        name: item.name,
        selling_price: item.selling_price,
        display_price: item.display_price || item.selling_price,
        discount_percent: item.discount_percent,
        description: item.description,
        vendor_id: item.vendor_id,
        vendor_name: item.vendor_name,
        quantity: qty,
        image: item.image || null,
        category: item.category
      });
    }
    localStorage.setItem("mealnoCart", JSON.stringify(currentCart));
    window.dispatchEvent(new Event("storage"));
    navigate("/menu", { state: { highlightedItemId: item.id } });
  };
  const filteredRestaurants = popularRestaurants.filter(item => {
    const query = searchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  });
  const displayRestaurants = filteredRestaurants.length > 0 ? filteredRestaurants : popularRestaurants;
  const renderHeroText = (text) => {
    const parts = text.split(/(trusted|healthy)/gi);
    return parts.map((part, i) =>
      /(trusted|healthy)/i.test(part) ?
        <span key={i} style={{ color: '#577A34', fontWeight: '800' }}>{part}</span>
        : part
    );
  };
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 5 }}>
      {/* 2. LOCATION SELECTOR */}
      <Box sx={{ px: 2, pb: 3 }}>
        <Box
          onClick={() => setOpenSheet(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <Avatar sx={{
            bgcolor: "#F8D548",
            mr: 1.5,
            width: 44,
            height: 44,
            mt: 1,
          }}>
            <LocationOnIcon sx={{ color: "#3B2F2A", fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ color: "#3A2C23", fontWeight: "900", display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.8rem' }}>
              {currentAddress?.address_type || "HOME"}
              <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.2, color: "#3A2C23" }} />
            </Typography>
            <Typography variant="caption" sx={{ color: "#6E655E", display: 'block', maxWidth: '250px', fontSize: '0.75rem', fontWeight: 500 }} noWrap>
              {currentAddress ? `${currentAddress.flat}, ${currentAddress.building}` : "Set your location"}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* 3. HERO SECTION */}
      <Box sx={{ px: 2, mt: -1 }}>
        <Typography
          key={heroIndex}
          variant="h6"
          fontWeight="900"
          sx={{
            mb: 3,
            minHeight: '2.4em', // FIX 1: Locks height to prevent layout shift between 1 and 2 lines
            lineHeight: 1.2,
            whiteSpace: "pre-line",
            color: "#3A2C23",
            animation: `${fadeText} 0.8s ease`
          }}
        >
          {renderHeroText(heroTexts[heroIndex])}
        </Typography>
        {/* 4. SEARCH BAR */}
        <Box sx={{ pb: 1, mt: -2 }}>
          <Paper
            elevation={0}
            sx={{
              p: '4px 12px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: "24px",
              height: 40,
              mb: 2,
              bgcolor: '#FFFFFF',
              border: 'none',
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <SearchIcon sx={{ color: '#7A7068', mr: 1.5 }} />
            <InputBase
              sx={{ flex: 1, fontWeight: 500, color: "#2F2A25", '&::placeholder': { color: '#A59D96' } }}
              placeholder="Search for your favorite food..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={() => setSearchTerm("")}
                sx={{ color: '#A59D96', p: 0.5, ml: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        </Box>
      </Box>
      {/* --- CONDITIONAL MENU RENDERING --- */}
      {!currentAddress ? (
        <Box
          sx={{
            px: 2,
            mt: 6,
            mb: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{
              bgcolor: '#FFFDF8',
              width: 80,
              height: 80,
              mb: 2,
              border: '2px dashed #E0D8C8'
            }}
          >
            <LocationOnIcon sx={{ color: '#F8C332', fontSize: 40 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", mb: 1 }}>
            Where are we delivering?
          </Typography>
          <Typography variant="body2" sx={{ color: "#8E867E", mb: 4, maxWidth: '280px' }}>
            Please select your address to see the menu and offers available in your area.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setOpenSheet(true)}
            sx={{
              borderRadius: "16px", py: 1.5, px: 4, fontWeight: "800",
              bgcolor: "#F8C332", color: "#3A2C23",
              '&:hover': { bgcolor: '#F5B71D' },
              textTransform: "none", fontSize: "1rem",
              boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)'
            }}
          >
            Select Address
          </Button>
        </Box>
      ) : (
        <>
          {/* 5. FOOD TAGS HORIZONTAL SCROLL */}
          <Box sx={{ pl: 2, py: 1, mt: -2, }}>
            {loadingTags ? (
              <Box sx={{ display: 'flex', gap: 2.5, pr: 2, overflowX: 'hidden' }}>
                {[1, 2, 3, 4, 5].map((skeleton) => (
                  <Box key={skeleton} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#E0D8C8', mb: 1 }} />
                    <Box sx={{ width: 50, height: 10, bgcolor: '#E0D8C8', borderRadius: 2 }} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  pr: 2,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' }
                }}
              >
                {foodTags.map((tag) => (
                  <Box
                    key={tag.id}
                    onClick={() => navigate('/menu', { state: { selectedCategory: tag.name } })}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: 55,
                      flexShrink: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: '#FFFDF8',
                        border: 'none',
                        p: 0.5,
                        mb: 1,
                        boxShadow: '0 4px 12px rgba(58, 44, 35, 0.04)'
                      }}
                    >
                      <Avatar
                        src={tag.image_url || ""}
                        alt={tag.name}
                        sx={{ width: '100%', height: '100%', bgcolor: '#D9D9D9', color: '#FFF' }}
                      >
                        {tag.name.charAt(0)}
                      </Avatar>
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight="800"
                      textAlign="center"
                      sx={{ color: '#3A2C23', lineHeight: 1.1, fontSize: '0.7rem' }}
                    >
                      {tag.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {/* 6. POPULAR NOW SECTION */}
          <Box sx={{ px: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23" }}>
                Popular Now Nearby
              </Typography>
              <Typography
                variant="body2"
                fontWeight="800"
                sx={{ color: "#577A34", cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}
                onClick={() => navigate('/menu')}
              >
                See all <ArrowForwardIosIcon sx={{ fontSize: '0.7rem', ml: 0.5 }} />
              </Typography>
            </Box>
            {searchTerm.trim() !== "" && filteredRestaurants.length === 0 && (
              <Box
                sx={{
                  mb: 2, p: 2, bgcolor: "#FFFFFF", borderRadius: "16px",
                  border: "1px solid #ECE5DA", boxShadow: "0 4px 16px rgba(58, 44, 35, 0.04)"
                }}
              >
                <Typography sx={{ color: "#E85D3F", fontWeight: 700, textAlign: "center" }}>
                  No exact match found for "{searchTerm}". Showing other popular foods nearby.
                </Typography>
              </Box>
            )}
            {loading && popularRestaurants.length === 0 ? (
              <Box sx={{ mt: 1 }}>
                <GlobalSkeleton type="default" />
                <GlobalSkeleton type="default" />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {[...displayRestaurants]
                  .sort((a, b) => {
                    if (a.can_order !== b.can_order) return a.can_order ? -1 : 1;
                    if (a.is_priority !== b.is_priority) return a.is_priority ? -1 : 1;
                    const rankA = a.sort_order || 0;
                    const rankB = b.sort_order || 0;
                    if (rankA !== rankB) {
                      return rankA - rankB;
                    }
                    if (a.name && b.name) {
                      return a.name.localeCompare(b.name);
                    }
                    return 0;
                  })
                  .map((res, index) => (
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} key={index}>
                      <Card
                        onClick={() => {
                          setSelectedItem(res);
                          setTempQuantity(1);
                        }}
                        sx={{
                          cursor: 'pointer',
                          width: '100%',
                          mb: 1.5,
                          borderRadius: '16px',
                          bgcolor: '#FFFFFF',
                          overflow: 'hidden',
                          border: 'none',
                          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                          }
                        }}
                      >
                        {/* 1. Flush Image Header */}
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '16 / 9',
                          }}
                        >
                          {res.image ? (
                            <>
                              <CardMedia
                                component="img"
                                image={res.image}
                                alt={res.name}
                                sx={{
                                  width: "100%", height: "100%", objectFit: "cover",
                                  filter: res.can_order ? "none" : "grayscale(100%)",
                                  opacity: res.can_order ? 1 : 0.6
                                }}
                              />
                              {!res.can_order && (
                                <Box
                                  sx={{
                                    position: "absolute", top: 12, right: 12,
                                    bgcolor: "rgba(58, 44, 35, 0.85)", color: "#FFFFFF",
                                    px: 1.5, py: 0.5, borderRadius: "12px",
                                    fontSize: 11, fontWeight: 700, zIndex: 2
                                  }}
                                >
                                  Not Available
                                </Box>
                              )}
                              {res.is_priority && (
                                <Box
                                  sx={{
                                    position: "absolute", top: 12, left: 12,
                                    bgcolor: "#F8C332", color: "#3A2C23",
                                    px: 1.5, py: 0.5, borderRadius: "8px",
                                    fontSize: 11, fontWeight: 800, zIndex: 2,
                                    display: "flex", alignItems: "center", gap: 0.5,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}
                                >
                                  ★ RECOMMENDED
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F2EEE8' }}>
                              <Typography color="#A59D96" fontWeight="700">No Image</Typography>
                            </Box>
                          )}
                        </Box>
                        {/* 2. Card Content */}
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                            {/* FIX 2: Changed alignment to flex-start so icon stays at top when text wraps */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                              <Box
                                sx={{
                                  width: 16, height: 16, flexShrink: 0, mt: 0.4, // Slight margin added to align with text baseline
                                  border: `2px solid ${res.category?.toLowerCase().startsWith('n') ? '#E85D3F' : '#6A9E3A'}`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  borderRadius: '4px',
                                  bgcolor: '#FFFFFF',
                                }}
                              >
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: res.category?.toLowerCase().startsWith('n') ? '#E85D3F' : '#6A9E3A' }} />
                              </Box>
                              {/* FIX (Issue 1): Removed line-clamp/ellipsis truncation so the full item
                                  name is always visible. minHeight keeps short names visually aligned
                                  with the fixed-ratio image above, while longer names simply wrap onto
                                  more lines instead of being cut off. */}
                              <Typography 
                                variant="h6" 
                                fontWeight="900" 
                                sx={{ 
                                  lineHeight: 1.3, 
                                  color: res.can_order ? "#2F2A25" : "#A59D96", 
                                  fontSize: '0.95rem',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {res.name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: "#8E867E", fontWeight: 500, fontSize: '0.85rem' }} noWrap>
                              {res.description || "curry"}
                            </Typography>
                          </Box>
                          {/* Right Side: Price & Minimalist Add Button */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ textAlign: 'right' }}>
                              {res.display_price > res.selling_price && (
                                <Typography sx={{ textDecoration: "line-through", color: "#A59D96", fontSize: 11, fontWeight: 600, display: 'block' }}>
                                  ₹{res.display_price}
                                </Typography>
                              )}
                              <Typography sx={{ color: "#2F2A25", fontWeight: 900, fontSize: 18 }}>
                                ₹{res.selling_price}
                              </Typography>
                              {/* FIX (Issue 2): Discount % badge, computed from display_price vs selling_price */}
                              {getDiscountPercent(res.display_price, res.selling_price) > 0 && (
                                <Typography
                                  sx={{
                                    color: "#E85D3F",
                                    fontWeight: 800,
                                    fontSize: 11,
                                    mt: 0.3,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {getDiscountPercent(res.display_price, res.selling_price)}% OFF
                                </Typography>
                              )}
                            </Box>
                            {/* Modern Minimalistic Add Button */}
                            <Button
                              disabled={!res.can_order}
                              variant="contained"
                              disableElevation
                              onClick={(e) => handleAddToCart(res, 1, e)}
                              sx={{
                                minWidth: '36px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                bgcolor: res.can_order ? '#E1EED3' : '#F2EEE8',
                                color: res.can_order ? '#577A34' : '#A59D96',
                                border: 'none',
                                boxShadow: 'none',
                                p: 0,
                                flexShrink: 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: res.can_order ? '#D5E6C0' : '#F2EEE8',
                                },
                                '&:active': {
                                  transform: res.can_order ? 'scale(0.92)' : 'none'
                                }
                              }}
                            >
                              {res.can_order ? <AddIcon sx={{ stroke: '#577A34', strokeWidth: 1 }} /> : <RemoveIcon />}
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        </>
      )}
      <Footer />
      {/* --- DRAWERS & SNACKBAR --- */}
      <AddressDrawer
        open={openSheet}
        onClose={() => setOpenSheet(false)}
        onSelect={(addr) => {
          setCurrentAddress(addr);
          localStorage.setItem("selectedAddress", JSON.stringify(addr));
          localStorage.removeItem("mealnoCart");
          setCart([]);
          setPopularRestaurants([]);
          setFoodTags([]);
          window.dispatchEvent(new Event("storage"));
          fetchPopularRestaurants(addr.lat, addr.lng);
          setOpenSheet(false);
        }}
      />
      <Drawer anchor="bottom" open={viewDetails} onClose={() => setViewDetails(false)} PaperProps={{ sx: { borderRadius: '24px 24px 0 0', p: 3, bgcolor: "#FFFFFF" } }}>
        <Typography variant="h6" fontWeight="800" sx={{ mb: 1, color: "#3A2C23" }}>Order Details</Typography>
        <Divider sx={{ borderColor: "#F2EEE8", mb: 2 }} />
        <List disablePadding>
          {cart.map((item) => (
            <ListItem key={item.id} disableGutters secondaryAction={
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <IconButton size="small" onClick={() => updateQty(item, -1)} sx={{ color: '#F29D17', bgcolor: '#FFF6DE' }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography fontWeight="700" sx={{ color: "#2F2A25" }}>{item.quantity}</Typography>
                <IconButton size="small" onClick={() => updateQty(item, 1)} sx={{ color: '#F29D17', bgcolor: '#FFF6DE' }}>
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography fontWeight="800" sx={{ ml: 2, minWidth: '50px', textAlign: 'right', color: "#2F2A25" }}>
                  ₹{item.selling_price * item.quantity}
                </Typography>
              </Stack>
            }>
              <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 700, color: "#3A2C23" }} />
            </ListItem>
          ))}
        </List>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={() => navigate("/cart")}
          sx={{
            mt: 4, py: 1.8, borderRadius: '16px',
            bgcolor: '#F8C332',
            color: '#3A2C23',
            '&:hover': { bgcolor: '#F5B71D' },
            fontWeight: '800',
            fontSize: '1rem',
            textTransform: 'none'
          }}
        >
          Proceed to Checkout
        </Button>
      </Drawer>
      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', borderRadius: '16px', fontWeight: 'bold', bgcolor: '#6A9E3A', color: '#FFFDF8' }}
        >
          Congratulations! Your order has been placed successfully.
        </Alert>
      </Snackbar>
      <Drawer
        anchor="bottom"
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        PaperProps={{
          sx: {
            borderRadius: "24px 24px 0 0", overflow: "visible",
            height: "75vh", maxHeight: "75vh", display: "flex",
            flexDirection: "column", bgcolor: "#FFFDF8"
          },
        }}
      >
        {selectedItem && (
          <>
            <IconButton
              onClick={() => setSelectedItem(null)}
              sx={{
                position: "absolute", top: -60, left: "50%", transform: "translateX(-50%)",
                bgcolor: "rgba(58, 44, 35, 0.7)", color: "#FFFFFF", zIndex: 1300,
                border: "1px solid rgba(255,255,255,0.2)",
                '&:hover': { bgcolor: "rgba(58, 44, 35, 0.9)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
              <Box sx={{ position: "relative", p: 0 }}>
                <CardMedia
                  component="img" height="240" image={selectedItem.image}
                  sx={{
                    borderRadius: "24px 24px 0 0",
                    objectFit: "cover",
                    boxShadow: '0 8px 24px rgba(58, 44, 35, 0.08)',
                    filter: selectedItem.can_order ? "none" : "grayscale(100%)",
                    opacity: selectedItem.can_order ? 1 : 0.6
                  }}
                />
                {selectedItem.is_priority && (
                  <Box
                    sx={{
                      position: "absolute", top: 16, left: 16,
                      bgcolor: "#F8C332", color: "#3A2C23",
                      px: 1.5, py: 0.5, borderRadius: "8px",
                      fontSize: 11, fontWeight: 800, zIndex: 2,
                      display: "flex", alignItems: "center", gap: 0.5,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }}
                  >
                    ★ RECOMMENDED
                  </Box>
                )}
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Box
                      sx={{
                        width: 18, height: 18, flexShrink: 0, mb: 1,
                        border: `2px solid ${selectedItem.category?.toLowerCase().startsWith("n") ? "#E85D3F" : "#6A9E3A"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        borderRadius: "4px",
                        bgcolor: "#FFFFFF"
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: selectedItem.category?.toLowerCase().startsWith("n") ? "#E85D3F" : "#6A9E3A" }} />
                    </Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: "#3A2C23", lineHeight: 1.3, fontSize: "1.45rem", wordBreak: "break-word", mb: 1 }}>
                      {selectedItem.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#6E655E", lineHeight: 1.4 }}>
                      {selectedItem.description || "Prepared fresh by our trusted home chefs. Authentic flavors packed with love."}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    {selectedItem.display_price > selectedItem.selling_price && (
                      <Typography sx={{ textDecoration: "line-through", color: "#A59D96", fontSize: 14, fontWeight: 600 }}>
                        ₹{selectedItem.display_price}
                      </Typography>
                    )}
                    <Typography sx={{ color: "#2F2A25", fontWeight: 800, fontSize: 22, lineHeight: 1.1 }}>
                      ₹{selectedItem.selling_price}
                    </Typography>
                    {selectedItem.discount_percent > 0 && (
                      <Typography sx={{ color: "#E85D3F", fontWeight: 800, fontSize: 12, bgcolor: '#FFF6DE', px: 1, py: 0.2, borderRadius: '8px', mt: 0.5, display: 'inline-block' }}>
                        {selectedItem.discount_percent}% OFF
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ p: 2, borderTop: "1px solid #ECE5DA", display: "flex", alignItems: "center", gap: 2, bgcolor: "#FFFFFF" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ border: "1px solid #ECE5DA", borderRadius: "16px", px: 1, py: 0.8, minWidth: "120px", bgcolor: "#FFFDF8" }}>
                <IconButton size="small" onClick={() => setTempQuantity((prev) => Math.max(1, prev - 1))} sx={{ color: "#F29D17" }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography fontWeight="800" sx={{ color: "#2F2A25", fontSize: '1.1rem' }}>{tempQuantity}</Typography>
                <IconButton size="small" onClick={() => setTempQuantity((prev) => prev + 1)} sx={{ color: "#F29D17" }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Button
                fullWidth
                disabled={!selectedItem.can_order}
                variant="contained"
                disableElevation
                onClick={() => {
                  handleAddToCart(selectedItem, tempQuantity);
                  setSelectedItem(null);
                }}
                sx={{
                  borderRadius: "16px", py: 1.5, fontWeight: "800",
                  bgcolor: "#F8C332", color: "#3A2C23",
                  '&:hover': { bgcolor: '#F5B71D' },
                  textTransform: "none", fontSize: "1rem",
                  boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)'
                }}
              >
                {selectedItem.can_order
                  ? `Add item - ₹${selectedItem.selling_price * tempQuantity}`
                  : "Currently Unavailable"}
              </Button>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
}
